import * as React from 'react';
import { t } from '@reos/i18n';
import type { ClientMapLayerId } from '@reos/duediligence';
import type { ClientMapLayerDto } from '../lib/parcel-map-api.js';
import {
  favouriteLayers,
  GROUP_EMPTY_NOTICE_KEY,
  GROUP_LABEL_KEY,
  groupThematicLayers,
  LAYER_LABEL_KEY,
  matchesLayerQuery,
  thematicLayers,
  type LayerVisibilityState,
  type ThematicLayerDto,
} from '../lib/map-layer-catalog.js';

/**
 * Krok MAP4 bod 2 — panel tematickych vrstev mapove karty: skupiny D1-D7,
 * hledani, dlazdice „Nejoblibenejsi", vice vrstev soucasne, legenda a
 * pruhlednost. Katalog je jen to, co prislo z registru pres
 * `GET /v1/duediligence/map/layers` (viz `lib/map-layer-catalog.ts`) -
 * zadny druhy seznam vrstev v klientovi.
 *
 * Komponenta je RIZENA: zapnute vrstvy a pruhlednost drzi `ParcelMap`, ktery
 * je promita do OL vrstev a do URL. Panel sam nic nenacita.
 */

export interface MapLayersPanelProps {
  readonly layers: readonly ClientMapLayerDto[];
  readonly state: LayerVisibilityState;
  readonly onToggle: (id: ClientMapLayerId, defaultOpacity: number) => void;
  readonly onOpacityChange: (id: ClientMapLayerId, opacity: number) => void;
  /** Aktualni zoom mapy - vrstva pod svym `min_zoom` upozorni, ze jeste neni videt. */
  readonly zoom: number | null;
}

function layerLabel(layer: ThematicLayerDto): string {
  return t(LAYER_LABEL_KEY[layer.id as ClientMapLayerId], { locale: 'cs' });
}

function LayerRow({
  layer,
  state,
  onToggle,
  onOpacityChange,
  zoom,
}: {
  readonly layer: ThematicLayerDto;
  readonly state: LayerVisibilityState;
  readonly onToggle: MapLayersPanelProps['onToggle'];
  readonly onOpacityChange: MapLayersPanelProps['onOpacityChange'];
  readonly zoom: number | null;
}): React.JSX.Element {
  const id = layer.id as ClientMapLayerId;
  const enabled = state.has(id);
  const opacity = state.get(id) ?? layer.map.default_opacity;
  const belowMinZoom = zoom !== null && zoom < layer.map.min_zoom;
  const testId = id.replace(/_/gu, '-');
  return (
    <li className="rh-a2-map-layers__row" data-testid={`map-layer-row-${testId}`}>
      <label className="rh-a2-map-layers__toggle">
        <input
          type="checkbox"
          checked={enabled}
          onChange={() => {
            onToggle(id, layer.map.default_opacity);
          }}
          data-testid={`map-layer-toggle-${testId}`}
        />
        <span>{layerLabel(layer)}</span>
      </label>
      <ul className="rh-a2-map-layers__legend" data-testid={`map-layer-legend-${testId}`}>
        {layer.map.legend.map((item) => (
          <li key={item.label}>
            <span
              className="rh-a2-map-layers__swatch"
              style={item.color === undefined ? undefined : { backgroundColor: item.color }}
              aria-hidden="true"
            />
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
      {enabled ? (
        <label className="rh-a2-map-layers__opacity">
          {t('webAdmin.parcelMap.layersPanel.opacity', { locale: 'cs' })}
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(opacity * 100)}
            onChange={(event) => {
              onOpacityChange(id, Number.parseInt(event.target.value, 10) / 100);
            }}
            data-testid={`map-layer-opacity-${testId}`}
          />
          <span data-testid={`map-layer-opacity-value-${testId}`}>
            {`${String(Math.round(opacity * 100))} %`}
          </span>
        </label>
      ) : null}
      {belowMinZoom ? (
        <p className="rh-a2-map-layers__hint" data-testid={`map-layer-minzoom-${testId}`}>
          {t('webAdmin.parcelMap.layersPanel.minZoom', {
            locale: 'cs',
            values: { zoom: String(layer.map.min_zoom) },
          })}
        </p>
      ) : null}
    </li>
  );
}

export function MapLayersPanel({
  layers,
  state,
  onToggle,
  onOpacityChange,
  zoom,
}: MapLayersPanelProps): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const groups = groupThematicLayers(layers);
  const favourites = favouriteLayers(layers);
  const searching = query.trim() !== '';
  const total = thematicLayers(layers).length;

  return (
    <section className="rh-a2-map-layers" data-testid="map-layers-panel">
      <h3 className="rh-a2-map-layers__heading">
        {t('webAdmin.parcelMap.layersPanel.heading', { locale: 'cs' })}
      </h3>
      <label className="rh-a2-map-layers__search">
        <span>{t('webAdmin.parcelMap.layersPanel.search.label', { locale: 'cs' })}</span>
        <input
          type="search"
          value={query}
          placeholder={t('webAdmin.parcelMap.layersPanel.search.placeholder', { locale: 'cs' })}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          data-testid="map-layers-search"
        />
      </label>

      {!searching && favourites.length > 0 ? (
        <div className="rh-a2-map-layers__favourites" data-testid="map-layers-favourites">
          <h4>{t('webAdmin.parcelMap.layersPanel.favourites', { locale: 'cs' })}</h4>
          <ul>
            {favourites.map((layer) => {
              const id = layer.id as ClientMapLayerId;
              const testId = id.replace(/_/gu, '-');
              return (
                <li key={id}>
                  <button
                    type="button"
                    className="rh-btn rh-a2-map-layers__tile"
                    aria-pressed={state.has(id)}
                    onClick={() => {
                      onToggle(id, layer.map.default_opacity);
                    }}
                    data-testid={`map-layer-favourite-${testId}`}
                  >
                    {layerLabel(layer)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {groups.map(({ group, layers: groupLayers }) => {
        const visible = groupLayers.filter((layer) => matchesLayerQuery(layerLabel(layer), query));
        const notice = GROUP_EMPTY_NOTICE_KEY[group];
        // Skupina bez vrstvy se vykresli jen tehdy, kdyz pro ni specifikace
        // predepisuje vysvetlujici text (D2 uzemni plan, D7 nabidky) - a pri
        // hledani ani ten ne, aby vysledek hledani neobsahoval prazdne oddily.
        if (visible.length === 0 && (searching || groupLayers.length > 0 || notice === undefined)) {
          return null;
        }
        return (
          <div
            key={group}
            className="rh-a2-map-layers__group"
            data-testid={`map-layers-group-${group}`}
          >
            <h4>{t(GROUP_LABEL_KEY[group], { locale: 'cs' })}</h4>
            {visible.length === 0 && notice !== undefined ? (
              <p className="rh-a2-map-layers__hint" data-testid={`map-layers-empty-${group}`}>
                {t(notice, { locale: 'cs' })}
              </p>
            ) : (
              <ul>
                {visible.map((layer) => (
                  <LayerRow
                    key={layer.id}
                    layer={layer}
                    state={state}
                    onToggle={onToggle}
                    onOpacityChange={onOpacityChange}
                    zoom={zoom}
                  />
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {searching &&
      groups.every(
        ({ layers: groupLayers }) =>
          groupLayers.filter((layer) => matchesLayerQuery(layerLabel(layer), query)).length === 0,
      ) ? (
        <p className="rh-a2-map-layers__hint" data-testid="map-layers-no-match">
          {t('webAdmin.parcelMap.layersPanel.noMatch', { locale: 'cs', values: { query } })}
        </p>
      ) : null}

      {total === 0 ? (
        <p className="rh-a2-map-layers__hint" data-testid="map-layers-empty">
          {t('webAdmin.parcelMap.layersPanel.emptyCatalog', { locale: 'cs' })}
        </p>
      ) : null}
    </section>
  );
}
